import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Camera, Plus, RefreshCw, Search, User, Calendar, X, Eye, EyeOff, Loader, Users, Wrench, ClipboardList, MessageSquare, UploadCloud, CornerUpLeft, BarChart3, Flag, ShieldCheck, Edit2, Trash2, Save, UserPlus, Mail, DollarSign, FileText, Send, CheckCircle, Package, Phone, Receipt, Menu, LogOut, Repeat, File, Bell, Paperclip, Eraser, Trophy, Award, TrendingUp, Star } from 'lucide-react';
import { format, formatDistanceToNow, addDays, addWeeks, addMonths, addYears, isAfter } from 'date-fns';
import ChatBox from './ChatBox';

const API_BASE = window.location.origin;
const ADMINS = ['Alex', 'Lorraine'];
const SYNC_INTERVAL = 30000; // 30 seconds for faster task updates
const ACTIVITY_TIMEOUT = 300000; // 5 minutes

// Recurrence options
const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'No Recurrence' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'sixMonths', label: 'Every 6 Months' },
  { value: 'yearly', label: 'Yearly' }
];

// Utility functions
const formatCurrency = (amount) => `R ${parseFloat(amount || 0).toFixed(2)}`;
const timeAgo = (d) => d ? formatDistanceToNow(new Date(d), { addSuffix: true }) : 'never';
const formatDateTime = (d) => d ? format(new Date(d), 'dd MMM, HH:mm') : '';

// Calculate next recurrence date
const getNextRecurrenceDate = (fromDate, recurrence, monthlyDay = null) => {
  const date = new Date(fromDate);
  switch (recurrence) {
    case 'daily': return addDays(date, 1);
    case 'weekly': return addWeeks(date, 1);
    case 'monthly':
      if (monthlyDay) {
        const nextMonth = addMonths(date, 1);
        nextMonth.setDate(Math.min(monthlyDay, new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0).getDate()));
        return nextMonth;
      }
      return addMonths(date, 1);
    case 'sixMonths': return addMonths(date, 6);
    case 'yearly': return addYears(date, 1);
    default: return null;
  }
};

// Upload Indicator Component
const UploadIndicator = ({ p, uploadingPurchase }) => {
  if (p) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: '#334155',
        color: 'white',
        padding: '0.75rem 1.5rem',
        borderRadius: '0.75rem',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        maxWidth: 'calc(100% - 2rem)',
        fontSize: '0.875rem'
      }}>
        <Loader size={20} style={{animation:'spin 1s linear infinite'}}/>
        <span>{p.status || `Uploading ${p.fileName} (${p.current}/${p.total})...`}</span>
      </div>
    );
  }

  if (uploadingPurchase) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: '#334155',
        color: 'white',
        padding: '0.75rem 1.5rem',
        borderRadius: '0.75rem',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        maxWidth: 'calc(100% - 2rem)',
        fontSize: '0.875rem'
      }}>
        <Loader size={20} style={{animation:'spin 1s linear infinite'}}/>
        <span>Uploading receipt/invoice...</span>
      </div>
    );
  }

  return null;
};

// Enhanced Task Item Component
const TaskItem = ({ task, onSelect, onUpdate, currentUser, hasNewNotes }) => {
  const isOverdue = !task.isComplete && task.dueDate && new Date(task.dueDate) < new Date();
  const totalAmount = (task.purchases || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  const photoCount = (task.photos || []).filter(p => !p.filename?.toLowerCase().endsWith('.pdf')).length;
  const documentCount = (task.photos || []).filter(p => p.filename?.toLowerCase().endsWith('.pdf')).length;

  const lastNote = task.notes && task.notes.length > 0
    ? task.notes[task.notes.length - 1]
    : null;

  const handleToggle = e => {
    e.stopPropagation();
    if (window.confirm(`Mark task as "${task.isComplete ? 'Incomplete' : 'Complete'}"?`)) {
      onUpdate(task.id, { isComplete: !task.isComplete });
    }
  };

  return (
    <div onClick={onSelect} className={`task-item ${task.isFlagged ? 'flagged' : ''} ${hasNewNotes ? 'has-new-notes' : ''}`} style={{
      background: task.isComplete ? '#f0fdf4' : isOverdue ? '#fef2f2' : hasNewNotes ? 'var(--new-note-bg)' : '#ffffff',
      border: `2px solid ${task.isComplete ? '#86efac' : isOverdue ? '#fecaca' : hasNewNotes ? 'var(--new-note)' : '#e5e7eb'}`,
      borderRadius: '0.75rem',
      padding: '0.875rem'
    }}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'0.75rem'}}>
        <input type="checkbox" checked={task.isComplete} onChange={handleToggle}
          className="task-checkbox" onClick={e=>e.stopPropagation()}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'0.25rem'}}>
            <h3 style={{margin:0,fontSize:'1rem',fontWeight:600}}>{task.title}</h3>
            {hasNewNotes && <div className="new-note-indicator" title="New notes added"/>}
            {task.isFlagged && <Flag size={14} color="var(--danger)"/>}
            {task.recurrence && task.recurrence !== 'none' && (
              <span className="recurrence-badge">
                <Repeat size={10}/>
                {RECURRENCE_OPTIONS.find(o => o.value === task.recurrence)?.label}
                {task.recurrence === 'monthly' && task.monthlyDay && ` (Day ${task.monthlyDay})`}
              </span>
            )}
          </div>
          <p style={{margin:'0 0 0.5rem',fontSize:'0.8125rem',color:'#6b7280'}}>{task.description}</p>

          {lastNote && (
            <div style={{
              background: '#f3f4f6',
              borderLeft: '3px solid #9ca3af',
              padding: '0.5rem',
              marginBottom: '0.5rem',
              borderRadius: '0.25rem'
            }}>
              <div style={{fontSize:'0.75rem',color:'#6b7280',marginBottom:'0.25rem'}}>
                <strong>{lastNote.author}</strong> • {timeAgo(lastNote.timestamp)}
              </div>
              <div style={{
                fontSize:'0.8125rem',
                color:'#374151',
                overflow:'hidden',
                textOverflow:'ellipsis',
                display:'-webkit-box',
                WebkitLineClamp:2,
                WebkitBoxOrient:'vertical',
                lineHeight:'1.4'
              }}>
                {lastNote.content}
              </div>
            </div>
          )}

          <div style={{display:'flex',flexWrap:'wrap',gap:'0.75rem',fontSize:'0.75rem'}}>
            <span style={{display:'flex',alignItems:'center',gap:'0.25rem'}}>
              <Users size={14}/>{task.assignedTo.join(', ')}
            </span>
            {task.dueDate && (
              <span style={{display:'flex',alignItems:'center',gap:'0.25rem'}}>
                <Calendar size={14}/>{format(new Date(task.dueDate),'dd MMM')}
              </span>
            )}
            {totalAmount > 0 && (
              <span style={{display:'flex',alignItems:'center',gap:'0.25rem',color:'var(--warning)'}}>
                <DollarSign size={14}/>{formatCurrency(totalAmount)}
              </span>
            )}
          </div>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:'0.25rem',alignItems:'flex-end'}}>
          {!!task.notes?.length && (
            <span style={{background:'#e0e7ff',color:'#4338ca',padding:'0.25rem 0.5rem',borderRadius:'99px',fontSize:'0.625rem',display:'flex',alignItems:'center',gap:'0.25rem'}}>
              <MessageSquare size={12}/>{task.notes.length}
            </span>
          )}
          {photoCount > 0 && (
            <span style={{background:'#ddd6fe',color:'#7c3aed',padding:'0.25rem 0.5rem',borderRadius:'99px',fontSize:'0.625rem',display:'flex',alignItems:'center',gap:'0.25rem'}}>
              <Camera size={12}/>{photoCount}
            </span>
          )}
          {documentCount > 0 && (
            <span style={{background:'#fee2e2',color:'#ef4444',padding:'0.25rem 0.5rem',borderRadius:'99px',fontSize:'0.625rem',display:'flex',alignItems:'center',gap:'0.25rem'}}>
              <File size={12}/>{documentCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Purchase Modal Component
const PurchaseModal = ({ purchase, onSave, onClose, currentUser }) => {
  const [item, setItem] = useState(purchase?.item || '');
  const [qty, setQty] = useState(purchase?.qty || 1);
  const [supplier, setSupplier] = useState(purchase?.supplier || '');
  const [amount, setAmount] = useState(purchase?.amount || '');
  const [category, setCategory] = useState(purchase?.category || 'General');
  const [stagedFile, setStagedFile] = useState(null);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      alert('Unsupported file type. Please upload an image (JPG, PNG, GIF, WebP) or PDF file.');
      return false;
    }
    if (file.size > maxSize) {
      alert('File too large. Maximum size is 10MB.');
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!item.trim()) {
      alert('Item description is required.');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      alert('An amount is required to log a purchase.');
      return;
    }

    const purchaseData = {
      id: purchase?.id || Date.now(),
      item: item.trim(),
      qty: parseInt(qty) || 1,
      supplier: supplier.trim(),
      amount: parseFloat(amount) || 0,
      category,
      addedBy: purchase?.addedBy || currentUser,
      timestamp: purchase?.timestamp || new Date().toISOString()
    };
    onSave(purchaseData, stagedFile);
  };

  const triggerFileInput = (capture) => {
    const input = fileInputRef.current;
    if (!input) return;
    input.value = '';
    input.setAttribute('accept', 'image/*,application/pdf');
    if (capture) {
      input.setAttribute('capture', 'environment');
    } else {
      input.removeAttribute('capture');
    }
    input.click();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{purchase ? 'Edit' : 'Log'} Purchase</h2>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          style={{display:'none'}}
          onChange={e => {
            const file = e.target.files?.[0];
            if (file && validateFile(file)) {
              setStagedFile(file);
            } else {
              setStagedFile(null);
            }
          }}
        />

        <div style={{display:'grid',gap:'0.75rem'}}>
          <div>
            <label className="form-label">Item Description*</label>
            <input type="text" className="form-input" value={item}
              onChange={e => setItem(e.target.value)}
              placeholder="e.g. Paint brushes, Safety gear, etc." required/>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
            <div>
              <label className="form-label">Quantity</label>
              <input type="number" className="form-input" value={qty}
                onChange={e => setQty(e.target.value)} min="1"/>
            </div>
            <div>
              <label className="form-label">Amount (R)*</label>
              <input type="number" className="form-input" value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00" step="0.01" min="0" required/>
            </div>
          </div>

          <div>
            <label className="form-label">Supplier</label>
            <input type="text" className="form-input" value={supplier}
              onChange={e => setSupplier(e.target.value)}
              placeholder="e.g. Builders Warehouse"/>
          </div>

          <div>
            <label className="form-label">Category</label>
            <select className="form-select" value={category}
              onChange={e => setCategory(e.target.value)}>
              <option>General</option>
              <option>Materials</option>
              <option>Equipment</option>
              <option>Labour</option>
              <option>Transport</option>
              <option>Other</option>
            </select>
          </div>

          <div>
            <label className="form-label">Receipt/Invoice Photo</label>
            <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
              <button type="button" onClick={() => triggerFileInput(true)} className="btn">
                <Camera size={16}/>Take Photo
              </button>
              <button type="button" onClick={() => triggerFileInput(false)} className="btn">
                <Receipt size={16}/>Upload
              </button>
            </div>
            {stagedFile && (
              <div style={{marginTop:'0.5rem',padding:'0.5rem',background:'var(--gray-100)',borderRadius:'0.375rem',fontSize:'0.8125rem'}}>
                📎 {stagedFile.name} ({stagedFile.type === 'application/pdf' ? 'PDF' : 'Image'})
              </div>
            )}
            {purchase?.proof && !stagedFile && (
              <div style={{marginTop:'0.5rem'}}>
                {purchase.proof.toLowerCase().endsWith('.pdf') ? (
                  <div style={{padding:'0.5rem',background:'var(--gray-100)',borderRadius:'0.375rem',fontSize:'0.8125rem'}}>
                    📄 PDF Receipt attached
                  </div>
                ) : (
                  <img src={`${API_BASE}/uploads/${purchase.proof}`} alt="Receipt"
                    style={{maxWidth:'100px',borderRadius:'0.375rem'}}/>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button onClick={handleSave} className="btn btn-primary">
            {purchase ? 'Save Changes' : 'Log Purchase'}
          </button>
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Enhanced Task Detail Modal
const TaskDetailModal = ({ task, onCreate, onUpdate, onPhotoUpload, setUploadingPurchase, ...props }) => {
  const { onClose, currentUser, isAdmin, setPhotoModalUrl, allUsers, onEmailTask, onCompleteTask, markTaskAsViewed, loadData } = props;
  const [isEditing, setIsEditing] = useState(!task);
  const [fields, setFields] = useState({
    title: task?.title || '',
    description: task?.description || '',
    priority: task?.priority || 'Medium',
    assignedTo: task?.assignedTo || [currentUser],
    dueDate: task?.dueDate || '',
    recurrence: task?.recurrence || 'none',
    monthlyDay: task?.monthlyDay || null
  });
  const [note, setNote] = useState('');
  const [stagedFiles, setStagedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [showDocumentNamePrompt, setShowDocumentNamePrompt] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pastedFile, setPastedFile] = useState(null);
  
  // State for note mentions
  const [isMentioning, setIsMentioning] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [filteredMentions, setFilteredMentions] = useState([]);

  useEffect(() => {
    if (task && markTaskAsViewed) {
      markTaskAsViewed(task.id);
    }
  }, [task, markTaskAsViewed]);

  const totalAmount = useMemo(() =>
    (task?.purchases || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
    [task?.purchases]
  );

  const taskPhotos = useMemo(() =>
    (task?.photos || []).filter(p => !p.filename?.toLowerCase().endsWith('.pdf')),
    [task?.photos]
  );

  const taskDocuments = useMemo(() =>
    (task?.photos || []).filter(p => p.filename?.toLowerCase().endsWith('.pdf')),
    [task?.photos]
  );
  
  const assignedUsersForMentions = useMemo(() => {
    if (!task) return allUsers;
    return allUsers.filter(u => task.assignedTo.includes(u));
  }, [task, allUsers]);

  const handleSave = () => {
    if (!fields.title.trim()) {
      alert('Title is required');
      return;
    }
    if (fields.assignedTo.length === 0) {
      alert('Please assign to at least one team member');
      return;
    }

    if (!fields.assignedTo.includes(currentUser)) {
      fields.assignedTo.push(currentUser);
    }

    if(task) {
      onUpdate(task.id, fields);
    } else {
      onCreate(fields, stagedFiles);
    }
    setIsEditing(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      const validFiles = files.filter(file => {
        if (!allowedTypes.includes(file.type)) {
          console.warn(`Skipped file ${file.name}: unsupported type ${file.type}`);
          return false;
        }
        if (file.size > 10 * 1024 * 1024) {
          console.warn(`Skipped file ${file.name}: exceeds 10MB limit`);
          return false;
        }
        return true;
      });

      if (validFiles.length !== files.length) {
        alert(`Some files were skipped. Only images (JPG, PNG, GIF, WebP) and PDFs under 10MB are allowed.`);
      }

      if (validFiles.length > 0) {
        const pdfFile = validFiles.find(f => f.type === 'application/pdf');

        if (!task) {
          setStagedFiles(prev => [...prev, ...validFiles]);
        } else {
          if (pdfFile) {
            setPendingFile(pdfFile);
            setShowDocumentNamePrompt(true);
            const nonPdfFiles = validFiles.filter(f => f.type !== 'application/pdf');
            if (nonPdfFiles.length > 0) {
              onPhotoUpload(task.id, nonPdfFiles);
            }
          } else {
            onPhotoUpload(task.id, validFiles);
          }
        }
      }
    }
    if (e.target.value) {
      e.target.value = '';
    }
  };

  const handleDocumentUpload = async () => {
    if (!pendingFile) return;

    const customName = documentName.trim() || pendingFile.name;
    const fileToUpload = pendingFile;
    fileToUpload.customName = customName;

    setUploadingPurchase(true);
    try {
        await onPhotoUpload(task.id, [fileToUpload]);
    } catch (error) {
      console.error("Document upload failed:", error);
      alert("Failed to upload document. Please check console for details.");
    } finally {
        setUploadingPurchase(false);
        setDocumentName('');
        setShowDocumentNamePrompt(false);
        setPendingFile(null);
    }
  };

  const triggerFileInput = (capture) => {
    const input = fileInputRef.current;
    if (!input) return;
    input.value = '';
    input.setAttribute('accept', 'image/*,application/pdf');
    if (capture) {
      input.setAttribute('capture', 'environment');
    } else {
      input.removeAttribute('capture');
    }
    input.click();
  };

  const handleAddNote = () => {
    if (!note.trim()) return;
    
    const mentionedUsers = [...new Set(note.match(/@(\w+)/g)?.map(m => m.substring(1)) || [])];
    const updates = {
      notes: [...(task.notes || []), {
        id: Date.now(),
        content: note,
        author: currentUser,
        timestamp: new Date().toISOString()
      }],
      timeline: [...(task.timeline || []), {
        id: Date.now(),
        type: 'note',
        user: currentUser,
        timestamp: new Date().toISOString(),
        content: note
      }]
    };

    onUpdate(task.id, updates, mentionedUsers);
    setNote('');
    setIsMentioning(false);
  };
  
  const handleNoteInputChange = (e) => {
    const value = e.target.value;
    setNote(value);
    const mentionMatch = value.match(/@(\w*)$/);

    if (mentionMatch) {
      const query = mentionMatch[1].toLowerCase();
      setIsMentioning(true);
      setFilteredMentions(assignedUsersForMentions.filter(u => u.toLowerCase().startsWith(query) && u !== currentUser));
      setMentionIndex(0);
    } else {
      setIsMentioning(false);
    }
  };

  const handleNoteMentionSelect = (name) => {
    const atIndex = note.lastIndexOf('@');
    const newNote = note.substring(0, atIndex) + `@${name} `;
    setNote(newNote);
    setIsMentioning(false);
  };
  
  const handleNoteKeyDown = (e) => {
    if (isMentioning && filteredMentions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(i => (i + 1) % filteredMentions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(i => (i - 1 + filteredMentions.length) % filteredMentions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        handleNoteMentionSelect(filteredMentions[mentionIndex]);
      }
    }
  };
  
  const handlePaste = (e) => {
    if (e.clipboardData.files && e.clipboardData.files.length > 0) {
        const file = e.clipboardData.files[0];
        if (file.type.startsWith('image/')) {
            e.preventDefault();
            setPastedFile(file);
        }
    }
  };

  const handleUploadPastedFile = async () => {
    if (pastedFile) {
        await onPhotoUpload(task.id, [pastedFile]);
        setPastedFile(null);
        if(loadData) loadData(); // Force a refresh of all app data
    }
  };

  const handleSavePurchase = async (purchaseData, photoFile) => {
    let proof = editingPurchase?.proof;

    if (photoFile) {
      setUploadingPurchase(true);
      const formData = new FormData();
      formData.append('photo', photoFile);
      formData.append('taskId', task.id);
      formData.append('user', currentUser);

      try {
        const response = await fetch(`${API_BASE}/api.php?action=uploadPhoto`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          if (result.filename) {
            proof = result.filename;
          } else if (result.error) {
            alert(result.error);
            setUploadingPurchase(false);
            return;
          }
        } else {
          const error = await response.json();
          alert(error.error || 'Upload failed');
          setUploadingPurchase(false);
          return;
        }
      } catch (e) {
        console.error('Upload error:', e);
        alert('Upload failed. Please try again.');
        setUploadingPurchase(false);
        return;
      }
      setUploadingPurchase(false);
    }

    const updatedPurchase = { ...purchaseData, proof };
    const purchases = task.purchases || [];

    if (editingPurchase) {
      const updated = purchases.map(p => p.id === editingPurchase.id ? updatedPurchase : p);
      onUpdate(task.id, { purchases: updated });
    } else {
      onUpdate(task.id, {
        purchases: [...purchases, updatedPurchase],
        timeline: [...(task.timeline || []), {
          id: Date.now(),
          type: 'purchase',
          user: currentUser,
          timestamp: new Date().toISOString(),
          content: `Logged purchase: ${purchaseData.item} - ${formatCurrency(purchaseData.amount)}`
        }]
      });
    }

    setShowPurchaseModal(false);
    setEditingPurchase(null);
  };

  const handleDeletePurchase = (purchase) => {
    if (window.confirm('Delete this purchase record?')) {
      const purchases = task.purchases.filter(p => p.id !== purchase.id);
      onUpdate(task.id, { purchases });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isEditing ? (task ? 'Edit Task' : 'Create New Task') : (task?.title || 'New Task')}
          </h2>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          style={{display:'none'}}
          onChange={handleFileChange}
        />

        {isEditing ? (
          <div>
            <div style={{display:'grid',gap:'0.75rem',marginBottom:'1rem'}}>
              <div>
                <label className="form-label">Title*</label>
                <input type="text" className="form-input" value={fields.title}
                  onChange={e => setFields(p => ({...p, title: e.target.value}))} required/>
              </div>
              <div>
                <label className="form-label">Description</label>
                <textarea className="form-textarea" value={fields.description}
                  onChange={e => setFields(p => ({...p, description: e.target.value}))} rows="3"/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
                <div>
                  <label className="form-label">Priority</label>
                  <select className="form-select" value={fields.priority}
                    onChange={e => setFields(p => ({...p, priority: e.target.value}))}>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Due Date</label>
                  <input type="date" className="form-input" value={fields.dueDate}
                    onChange={e => setFields(p => ({...p, dueDate: e.target.value}))}/>
                </div>
              </div>
              <div>
                <label className="form-label">Recurrence</label>
                <div style={{display:'grid',gridTemplateColumns:fields.recurrence === 'monthly' ? '2fr 1fr' : '1fr',gap:'0.75rem'}}>
                  <select className="form-select" value={fields.recurrence}
                    onChange={e => setFields(p => ({...p, recurrence: e.target.value, monthlyDay: null}))}>
                    {RECURRENCE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {fields.recurrence === 'monthly' && (
                    <select className="form-select" value={fields.monthlyDay || ''}
                      onChange={e => setFields(p => ({...p, monthlyDay: e.target.value ? parseInt(e.target.value) : null}))}>
                      <option value="">Same day each month</option>
                      {[...Array(31)].map((_, i) => (
                        <option key={i+1} value={i+1}>Day {i+1}</option>
                      ))}
                    </select>
                  )}
                </div>
                {fields.recurrence !== 'none' && fields.dueDate && (
                  <p style={{fontSize:'0.75rem',color:'#6b7280',marginTop:'0.25rem'}}>
                    Next occurrence: {format(getNextRecurrenceDate(fields.dueDate, fields.recurrence, fields.monthlyDay), 'dd MMM, HH:mm')}
                  </p>
                )}
              </div>
              <div>
                <label className="form-label">Assign To* {!task && <span style={{fontSize:'0.75rem',color:'#6b7280'}}>(You will be auto-assigned)</span>}</label>
                {allUsers.length > 0 ? (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(100px,1fr))',gap:'0.375rem'}}>
                    {allUsers.map(userName => (
                      <label key={userName} style={{display:'flex',alignItems:'center',gap:'0.375rem',fontSize:'0.875rem'}}>
                        <input type="checkbox" checked={fields.assignedTo.includes(userName)}
                          onChange={e => setFields(p => ({
                            ...p,
                            assignedTo: e.target.checked
                              ? [...p.assignedTo, userName]
                              : p.assignedTo.filter(am => am !== userName)
                          }))}/>
                        <span>{userName}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p style={{color:'var(--danger)',fontSize:'0.875rem'}}>No active users available.</p>
                )}
              </div>
            </div>
            {!task && (
              <div>
                <label className="form-label">Add Initial Photos/Documents</label>
                <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
                  <button type="button" onClick={() => triggerFileInput(true)} className="btn">
                    <Camera size={16}/>Take Photo
                  </button>
                  <button type="button" onClick={() => triggerFileInput(false)} className="btn">
                    <UploadCloud size={16}/>Upload Files
                  </button>
                </div>
                {stagedFiles.length > 0 && (
                  <div className="photo-grid">
                    {stagedFiles.map((file, i) => (
                      <div key={i} className="photo-preview">
                        {file.type === 'application/pdf' ? (
                          <div className="document-preview">
                            <FileText size={32} color="#ef4444"/>
                            <span style={{position:'absolute',bottom:'0.25rem',fontSize:'0.6rem',textAlign:'center',width:'100%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'#991b1b'}}>
                                {file.name}
                            </span>
                          </div>
                        ) : (
                          <img src={URL.createObjectURL(file)} alt="Preview"/>
                        )}
                        <button onClick={() => setStagedFiles(p => p.filter((_, idx) => idx !== i))}
                          className="photo-remove">
                          <X size={14}/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          task && (
            <div>
              <div className="tab-container">
                <button onClick={() => setActiveTab('timeline')}
                  className={`tab-button ${activeTab === 'timeline' ? 'active' : ''}`}>
                  Timeline
                </button>
                <button onClick={() => setActiveTab('documents')}
                  className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}>
                  Documents {taskDocuments.length > 0 && `(${taskDocuments.length})`}
                </button>
                <button onClick={() => setActiveTab('purchases')}
                  className={`tab-button ${activeTab === 'purchases' ? 'active' : ''}`}>
                  Purchases {totalAmount > 0 && `(${formatCurrency(totalAmount)})`}
                </button>
              </div>

              {activeTab === 'timeline' && (
                <div>
                  <div style={{background:'#f8f9fa',padding:'0.875rem',borderRadius:'0.5rem',marginBottom:'0.75rem'}}>
                    <p style={{margin:0,whiteSpace:'pre-wrap',wordBreak:'break-word',fontSize:'0.875rem'}}>
                      {task.description || 'No description.'}
                    </p>
                    {task.recurrence && task.recurrence !== 'none' && (
                      <div style={{marginTop:'0.5rem',display:'flex',alignItems:'center',gap:'0.375rem',fontSize:'0.75rem',color:'#6b7280'}}>
                        <Repeat size={14}/>
                        Recurring: {RECURRENCE_OPTIONS.find(o => o.value === task.recurrence)?.label}
                        {task.recurrence === 'monthly' && task.monthlyDay && ` (Day ${task.monthlyDay})`}
                      </div>
                    )}
                  </div>
                  
                  <div className="note-input-wrapper">
                    {isMentioning && filteredMentions.length > 0 && (
                        <div className="mention-list">
                        {filteredMentions.map((user, idx) => (
                            <div key={user} className={`mention-item ${idx === mentionIndex ? 'selected' : ''}`} onClick={() => handleNoteMentionSelect(user)}>
                            {user}
                            </div>
                        ))}
                        </div>
                    )}
                    <textarea value={note} onChange={handleNoteInputChange} onKeyDown={handleNoteKeyDown} onPaste={handlePaste}
                      placeholder="Add a note, @mention a user and email task reminder, or paste a screenshot..." className="form-textarea" rows="2"
                      style={{marginBottom:'0.5rem',fontSize:'0.875rem'}}/>
                    
                    {pastedFile && (
                        <div style={{ margin: '0.5rem 0', padding: '0.5rem', background: '#e0f2fe', borderRadius: '0.5rem' }}>
                            <div style={{ fontWeight: '600', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>Pasted Image Preview:</div>
                            <img src={URL.createObjectURL(pastedFile)} alt="Pasted preview" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '0.375rem', marginBottom: '0.5rem' }}/>
                            <div style={{display:'flex', gap:'0.5rem'}}>
                                <button onClick={handleUploadPastedFile} className="btn btn-primary"><Paperclip size={16}/>Upload Screenshot</button>
                                <button onClick={() => setPastedFile(null)} className="btn btn-secondary"><X size={16}/>Clear</button>
                            </div>
                        </div>
                    )}

                    <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
                      <button type="button" onClick={() => triggerFileInput(true)} className="btn">
                        <Camera size={16}/>Photo
                      </button>
                      <button type="button" onClick={() => triggerFileInput(false)} className="btn">
                        <UploadCloud size={16}/>Upload
                      </button>
                      <button onClick={handleAddNote} className="btn btn-primary" disabled={!note.trim()}>
                        <MessageSquare size={16}/>Add Note
                      </button>
                    </div>
                  </div>

                  <div style={{marginTop:'0.75rem'}}>
                    {[...(task.timeline || [])].sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)).map(entry => (
                      <div key={entry.id} style={{
                        marginBottom:'0.75rem',
                        paddingLeft:'1.5rem',
                        borderLeft:'2px solid #e5e7eb',
                        fontSize:'0.8125rem'
                      }}>
                        <div style={{marginLeft:'-1.75rem',marginBottom:'0.25rem',fontSize:'0.75rem',color:'#6b7280'}}>
                          <strong>{entry.user}</strong> - {formatDateTime(entry.timestamp)}
                        </div>
                        <div>{entry.content}</div>
                        {entry.type === 'photo' && entry.content && (
                          entry.content.toLowerCase().endsWith('.pdf') ? (
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              marginTop: '0.375rem',
                              padding: '0.5rem',
                              background: '#fee2e2',
                              borderRadius: '0.375rem',
                              cursor: 'pointer'
                            }} onClick={() => window.open(`${API_BASE}/uploads/${entry.content}`, '_blank')}>
                              <FileText size={16} color="#ef4444"/>
                              <span style={{fontSize: '0.75rem', color: '#991b1b'}}>
                                {entry.displayName || 'PDF Document'}
                              </span>
                            </div>
                          ) : (
                            <img src={`${API_BASE}/uploads/${entry.content}`} alt="Timeline"
                              style={{maxWidth:150,borderRadius:'0.375rem',cursor:'pointer',marginTop:'0.375rem'}}
                              onClick={() => setPhotoModalUrl(`${API_BASE}/uploads/${entry.content}`)}/>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'documents' && (
                <div>
                  <div style={{marginBottom:'0.75rem'}}>
                    <button onClick={() => triggerFileInput(false)} className="btn btn-primary" style={{width:'100%'}}>
                      <UploadCloud size={16}/>Upload Document
                    </button>
                  </div>

                  {taskDocuments.length > 0 ? (
                    <div style={{display:'grid',gap:'0.75rem'}}>
                      {taskDocuments.map((doc, idx) => (
                        <div key={idx} style={{
                          display:'flex',
                          alignItems:'center',
                          gap:'0.75rem',
                          padding:'0.75rem',
                          background:'var(--gray-100)',
                          borderRadius:'0.5rem',
                          cursor:'pointer'
                        }} onClick={() => window.open(`${API_BASE}/uploads/${doc.filename}`, '_blank')}>
                          <FileText size={24} color="#ef4444"/>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:600,fontSize:'0.875rem'}}>
                              {doc.displayName || doc.filename}
                            </div>
                            <div style={{fontSize:'0.75rem',color:'#6b7280'}}>
                              Uploaded by {doc.uploadedBy} • {timeAgo(doc.uploadedAt)}
                            </div>
                          </div>
                          <button className="btn" style={{padding:'0.5rem'}}>
                            <Eye size={16}/>View
                          </button>
                        </div>
                      ))}

                      {taskPhotos.length > 0 && (
                        <>
                          <div style={{fontSize:'0.875rem',fontWeight:600,marginTop:'1rem'}}>Photos</div>
                          <div className="photo-grid">
                            {taskPhotos.map((photo, idx) => (
                              <div key={idx} className="photo-preview">
                                <img src={`${API_BASE}/uploads/${photo.filename}`} alt="Task photo"
                                  onClick={() => setPhotoModalUrl(`${API_BASE}/uploads/${photo.filename}`)}/>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <p style={{color:'#6b7280',textAlign:'center',marginTop:'2rem',fontSize:'0.875rem'}}>
                      No documents uploaded yet.
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'purchases' && (
                <div>
                  <button onClick={() => { setEditingPurchase(null); setShowPurchaseModal(true); }}
                    className="btn btn-primary" style={{marginBottom:'0.75rem',width:'100%'}}>
                    <Plus size={16}/>Log Purchase
                  </button>

                  {task.purchases?.length > 0 ? (
                    <div>
                      {task.purchases.map(p => (
                        <div key={p.id} className="purchase-item">
                          <div className="purchase-content">
                            <div className="purchase-header">
                              <div className="purchase-title">
                                {p.qty}x {p.item}
                              </div>
                              {p.amount > 0 && <div className="purchase-amount">{formatCurrency(p.amount)}</div>}
                            </div>
                            <div className="purchase-details">
                              {p.supplier && <span>{p.supplier} • </span>}
                              {p.category} • {p.addedBy}
                            </div>
                          </div>
                          {p.proof && (
                            p.proof.toLowerCase().endsWith('.pdf') ? (
                              <div style={{
                                width: '50px',
                                height: '50px',
                                background: '#ef4444',
                                borderRadius: '0.375rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                flexShrink: 0
                              }} onClick={() => window.open(`${API_BASE}/uploads/${p.proof}`, '_blank')}>
                                <FileText size={24} color="white"/>
                              </div>
                            ) : (
                              <img src={`${API_BASE}/uploads/${p.proof}`} alt="Receipt"
                                className="purchase-proof"
                                onClick={() => setPhotoModalUrl(`${API_BASE}/uploads/${p.proof}`)}/>
                            )
                          )}
                          {isAdmin && (
                            <div style={{display:'flex',flexDirection:'column',gap:'0.25rem'}}>
                              <button onClick={() => { setEditingPurchase(p); setShowPurchaseModal(true); }}
                                className="btn" style={{padding:'0.375rem',fontSize:'0.75rem'}}>
                                <Edit2 size={14}/>
                              </button>
                              <button onClick={() => handleDeletePurchase(p)}
                                className="btn" style={{padding:'0.375rem',fontSize:'0.75rem',background:'var(--danger)',color:'white'}}>
                                <Trash2 size={14}/>
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      <div style={{
                        marginTop:'0.75rem',
                        padding:'0.75rem',
                        background:'var(--gray-100)',
                        borderRadius:'0.5rem',
                        display:'flex',
                        justifyContent:'space-between',
                        alignItems:'center'
                      }}>
                        <span style={{fontWeight:600}}>Total:</span>
                        <span style={{fontSize:'1.125rem',fontWeight:700,color:'var(--warning)'}}>
                          {formatCurrency(totalAmount)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p style={{color:'#6b7280',textAlign:'center',marginTop:'2rem',fontSize:'0.875rem'}}>
                      No purchases logged yet.
                    </p>
                  )}
                </div>
              )}
            </div>
          )
        )}

        {/* Document Naming Modal */}
        {showDocumentNamePrompt && (
          <div style={{
            position:'fixed',
            inset:0,
            background:'rgba(0,0,0,0.5)',
            display:'flex',
            alignItems:'center',
            justifyContent:'center',
            zIndex:60
          }} onClick={() => setShowDocumentNamePrompt(false)}>
            <div style={{
              background:'white',
              borderRadius:'0.75rem',
              padding:'1.5rem',
              width:'90%',
              maxWidth:'400px',
              boxShadow:'0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{marginTop:0,fontSize:'1.125rem'}}>Name Your Document</h3>
              <p style={{fontSize:'0.875rem',color:'#6b7280',marginBottom:'1rem'}}>
                Give this document a descriptive name to help identify it later.
              </p>
              <input
                type="text"
                className="form-input"
                value={documentName}
                onChange={e => setDocumentName(e.target.value)}
                placeholder={pendingFile?.name || 'Document name...'}
                autoFocus
                onKeyPress={e => e.key === 'Enter' && handleDocumentUpload()}
              />
              <div style={{display:'flex',gap:'0.5rem',marginTop:'1rem'}}>
                <button onClick={handleDocumentUpload} className="btn btn-primary">
                  <Save size={16}/>Save
                </button>
                <button onClick={() => {
                  setShowDocumentNamePrompt(false);
                  setDocumentName('');
                  setPendingFile(null);
                }} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="modal-actions">
          {isEditing ? (
            <>
              <button onClick={handleSave} className="btn btn-primary">
                {task ? 'Save Changes' : 'Create Task'}
              </button>
              {task && (
                <button onClick={() => {
                  setIsEditing(false);
                  setFields({
                    title: task.title,
                    description: task.description,
                    priority: task.priority,
                    assignedTo: task.assignedTo,
                    dueDate: task.dueDate,
                    recurrence: task.recurrence || 'none',
                    monthlyDay: task.monthlyDay || null
                  });
                }} className="btn btn-secondary">
                  Cancel
                </button>
              )}
            </>
          ) : (
            <>
              {isAdmin && task && (
                <button onClick={() => setIsEditing(true)} className="btn">
                  <Edit2 size={16}/>Edit
                </button>
              )}
              {task && !task.isComplete && (
                <button onClick={() => onCompleteTask(task)} className="btn btn-success">
                  <CheckCircle size={16}/>Complete
                </button>
              )}
              {isAdmin && task?.isComplete && (
                <button onClick={() => onUpdate(task.id, { isComplete: false })}
                  className="btn" style={{background:'var(--warning)', color:'white'}}>
                  <CornerUpLeft size={16}/>Reopen
                </button>
              )}
              {isAdmin && task && (
                <>
                  <button onClick={() => onUpdate(task.id, { isFlagged: !task.isFlagged })}
                    className="btn" style={{background: task.isFlagged ? 'var(--danger)' : 'var(--warning)', color: 'white'}}>
                    <Flag size={16}/>{task.isFlagged ? 'Unflag' : 'Flag'}
                  </button>
                  <button onClick={() => onEmailTask(task)} className="btn btn-admin">
                    <Mail size={16}/>Email
                  </button>
                </>
              )}
            </>
          )}

          <button onClick={onClose} className="btn btn-secondary">
            <X size={16}/>Close
          </button>
        </div>
      </div>

      {showPurchaseModal && (
        <PurchaseModal
          purchase={editingPurchase}
          onSave={handleSavePurchase}
          onClose={() => { setShowPurchaseModal(false); setEditingPurchase(null); }}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

// Admin Panel Component
const AdminPanel = ({ data, onUpdateData, onClose, currentUser }) => {
  const { users } = data;
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', phone: '' });
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '', phone: '', role: 'user' });
  const [showEditPassword, setShowEditPassword] = useState(false);

  const handleAddUser = (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.password) {
      alert('Name, email and password are required');
      return;
    }
    if (users.find(u => u.name.toLowerCase() === newUser.name.toLowerCase())) {
      alert('Username already exists');
      return;
    }
    const user = {
      id: Date.now(),
      name: newUser.name,
      email: newUser.email,
      password: newUser.password,
      phone: newUser.phone,
      isActive: true,
      role: 'user'
    };
    onUpdateData({ ...data, users: [...users, user] });
    setNewUser({ name: '', email: '', password: '', phone: '' });
    setShowAddUser(false);
  };

  const handleEditUser = (user) => {
    setEditingUser(user.id);
    setEditForm({
      name: user.name,
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      role: user.role || 'user'
    });
    setShowEditPassword(false);
  };

  const handleSaveEdit = () => {
    const updatedUsers = users.map(u => {
      if (u.id === editingUser) {
        const updates = {
          ...u,
          name: editForm.name,
          email: editForm.email,
          phone: editForm.phone,
          role: editForm.role,
        };
        if (editForm.password && editForm.password.trim() !== '') {
          updates.password = editForm.password;
        }
        return updates;
      }
      return u;
    });

    onUpdateData({ ...data, users: updatedUsers });
    setEditingUser(null);
    setEditForm({ name: '', email: '', password: '', phone: '', role: 'user' });
  };

  const handleToggleActive = (userId) => {
    const updatedUsers = users.map(u =>
      u.id === userId ? { ...u, isActive: !u.isActive } : u
    );
    onUpdateData({ ...data, users: updatedUsers });
  };

  const handleDeleteUser = (userId, userName) => {
    if (ADMINS.includes(userName)) {
      alert('Cannot delete admin users');
      return;
    }
    if (window.confirm(`Delete user "${userName}"?`)) {
      onUpdateData({ ...data, users: users.filter(u => u.id !== userId) });
    }
  };

  const handleClearChat = async (period) => {
    let confirmMessage = '';
    switch(period) {
        case 'day': confirmMessage = 'Are you sure you want to delete all chat messages from the last 24 hours?'; break;
        case '2days': confirmMessage = 'Are you sure you want to delete all chat messages from the last 48 hours?'; break;
        case 'all': confirmMessage = 'DANGER: Are you sure you want to permanently delete the ENTIRE chat history? This cannot be undone.'; break;
        default: return;
    }

    if (window.confirm(confirmMessage)) {
      try {
        const response = await fetch(`${API_BASE}/api.php?action=clearChat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ period: period, user: currentUser })
        });
        if (response.ok) {
            alert('Chat history has been cleared. Please refresh the page to see the changes.');
        } else {
            const err = await response.json();
            throw new Error(err.error || 'Failed to clear chat.');
        }
      } catch (error) {
        alert(`Error clearing chat: ${error.message}`);
      }
    }
  };

  // Helper function to count old completed tasks
  const getOldTaskCount = (months) => {
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - months);
    
    return data.tasks.filter(task => {
      if (!task.isComplete) return false; // Only count completed tasks
      
      // Use completedAt if available, otherwise fall back to creation date or timeline
      let taskDate = null;
      
      if (task.completedAt) {
        taskDate = new Date(task.completedAt);
      } else if (task.timeline) {
        // Find completion entry in timeline
        const completionEntry = task.timeline.find(entry => entry.type === 'completed');
        if (completionEntry) {
          taskDate = new Date(completionEntry.timestamp);
        }
      }
      
      // If we still don't have a date, use creation date as fallback
      if (!taskDate && task.createdAt) {
        taskDate = new Date(task.createdAt);
      }
      
      return taskDate && taskDate < cutoffDate;
    }).length;
  };

  // Handler for deleting old tasks
  const handleDeleteOldTasks = (months) => {
    const count = getOldTaskCount(months);
    if (count === 0) {
      alert(`No completed tasks older than ${months} month(s) found.`);
      return;
    }

    const confirmMessage = `⚠️ DANGER: Delete ${count} completed task(s) older than ${months} month(s)?\n\nThis will permanently delete:\n• Task data and descriptions\n• All photos and documents\n• All notes and comments\n• Purchase records\n• Timeline history\n\nThis action CANNOT be undone!\n\nType "DELETE" to confirm:`;
    
    const userInput = prompt(confirmMessage);
    if (userInput !== 'DELETE') {
      if (userInput !== null) { // User didn't cancel
        alert('Deletion cancelled. You must type "DELETE" exactly to confirm.');
      }
      return;
    }

    // Perform the deletion
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - months);
    
    const tasksToKeep = data.tasks.filter(task => {
      if (!task.isComplete) return true; // Keep all incomplete tasks
      
      // Check if task is old enough to delete
      let taskDate = null;
      
      if (task.completedAt) {
        taskDate = new Date(task.completedAt);
      } else if (task.timeline) {
        const completionEntry = task.timeline.find(entry => entry.type === 'completed');
        if (completionEntry) {
          taskDate = new Date(completionEntry.timestamp);
        }
      }
      
      if (!taskDate && task.createdAt) {
        taskDate = new Date(task.createdAt);
      }
      
      // Keep task if it's not old enough or we can't determine its age
      return !taskDate || taskDate >= cutoffDate;
    });

    // Update data
    onUpdateData({ ...data, tasks: tasksToKeep });
    
    const deletedCount = data.tasks.length - tasksToKeep.length;
    alert(`Successfully deleted ${deletedCount} old completed task(s) and their associated data.`);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{maxWidth:'1000px'}} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title" style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
            <ShieldCheck size={20} color="var(--admin)"/>Admin Panel
          </h2>
        </div>

        <div className="card" style={{background:'#f8f9fa', marginBottom:'1rem'}}>
            <h3 style={{marginTop:0,fontSize:'1.125rem'}}>User Management</h3>
            <button onClick={() => setShowAddUser(true)} className="btn btn-primary">
              <UserPlus size={16}/>Add New User
            </button>
        </div>

        {showAddUser && (
          <div className="card" style={{background:'#f8f9fa',marginBottom:'0.75rem'}}>
            <h3 style={{marginTop:0,fontSize:'1.125rem'}}>Add New User Form</h3>
            <form onSubmit={handleAddUser}>
              <div style={{display:'grid',gap:'0.75rem',marginBottom:'0.75rem'}}>
                <div>
                  <label className="form-label">Username</label>
                  <input type="text" className="form-input" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} required />
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <input type="email" className="form-input" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} required />
                </div>
                <div>
                  <label className="form-label">Phone</label>
                  <input type="tel" className="form-input" value={newUser.phone} onChange={e => setNewUser({...newUser, phone: e.target.value})} placeholder="+27 XX XXX XXXX" />
                </div>
                <div>
                  <label className="form-label">Initial Password</label>
                  <input type="password" className="form-input" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} required />
                </div>
              </div>
              <div style={{display:'flex',gap:'0.5rem'}}>
                <button type="submit" className="btn btn-primary"><Save size={16}/>Create User</button>
                <button type="button" onClick={() => setShowAddUser(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div style={{overflowX:'auto', marginBottom:'1.5rem'}}>
          <table className="user-table" style={{fontSize:'0.875rem'}}>
            <thead>
              <tr><th>Username</th><th>Email</th><th>Role</th><th>Status</th><th>Password</th><th style={{textAlign:'right'}}>Actions</th></tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  {editingUser === user.id ? (
                    <>
                      <td><input type="text" className="form-input" style={{padding:'0.5rem',fontSize:'0.8125rem'}} value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></td>
                      <td><input type="email" className="form-input" style={{padding:'0.5rem',fontSize:'0.8125rem'}} value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} /></td>
                      <td>
                        <select className="form-select" style={{padding:'0.5rem',fontSize:'0.8125rem'}} value={editForm.role} onChange={e => setEditForm({...editForm, role: e.target.value})} disabled={currentUser === user.name || ADMINS.includes(user.name)}>
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td><span className={`user-status ${user.isActive ? 'active' : 'inactive'}`}>{user.isActive ? '✓ Active' : '✗ Inactive'}</span></td>
                      <td>
                        <div style={{position:'relative',display:'flex',alignItems:'center'}}>
                          <input type={showEditPassword ? "text" : "password"} className="form-input" style={{padding:'0.5rem',fontSize:'0.8125rem', paddingRight:'2.5rem'}} value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})} placeholder="New password..."/>
                          <button type="button" onClick={() => setShowEditPassword(!showEditPassword)} style={{position:'absolute', right:'0.25rem', top:'50%', transform:'translateY(-50%)', background:'transparent', border:'none', color:'#6b7280', cursor:'pointer', padding:'0.25rem' }}>{showEditPassword ? <EyeOff size={16}/> : <Eye size={16}/>}</button>
                        </div>
                      </td>
                      <td><div className="user-actions"><button onClick={handleSaveEdit} className="btn btn-success" style={{padding:'0.375rem'}}><Save size={14}/></button><button onClick={() => setEditingUser(null)} className="btn" style={{padding:'0.375rem'}}><X size={14}/></button></div></td>
                    </>
                  ) : (
                    <>
                      <td style={{fontWeight:600}}>{user.name}</td>
                      <td>{user.email || '-'}</td>
                      <td><span className={`user-role ${user.role === 'admin' ? 'admin' : 'user'}`}>{user.role}</span></td>
                      <td><span className={`user-status ${user.isActive ? 'active' : 'inactive'}`}>{user.isActive ? '✓ Active' : '✗ Inactive'}</span></td>
                      <td>••••••••</td>
                      <td><div className="user-actions"><button onClick={() => handleEditUser(user)} className="btn" style={{padding:'0.375rem'}} title="Edit"><Edit2 size={14}/></button><button onClick={() => handleToggleActive(user.id)} className="btn" style={{padding:'0.375rem', background:user.isActive?'var(--warning)':'var(--success)',color:'white'}} title={user.isActive ? 'Deactivate' : 'Activate'}>{user.isActive ? '🔒' : '🔓'}</button>{!ADMINS.includes(user.name) && (<button onClick={() => handleDeleteUser(user.id, user.name)} className="btn btn-danger" style={{padding:'0.375rem'}} title="Delete"><Trash2 size={14}/></button>)}</div></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="card" style={{background:'#fffbe6', border:'1px solid #facc15', marginBottom:'1.5rem'}}>
            <h3 style={{marginTop:0,fontSize:'1.125rem', display:'flex', alignItems:'center', gap:'0.5rem'}}><Eraser color="var(--warning)"/>Chat Management</h3>
            <p style={{fontSize:'0.875rem', color: '#a16207', marginTop:0}}>These actions permanently delete messages from the server.</p>
            <div style={{display:'flex', gap:'0.5rem', flexWrap:'wrap'}}>
                <button onClick={() => handleClearChat('day')} className="btn btn-warning"><Trash2 size={16}/>Clear Last 24 Hours</button>
                <button onClick={() => handleClearChat('2days')} className="btn btn-warning"><Trash2 size={16}/>Clear Last 48 Hours</button>
                <button onClick={() => handleClearChat('all')} className="btn btn-danger"><Trash2 size={16}/>Clear All Chat History</button>
            </div>
        </div>

        <div className="card" style={{background:'#fef2f2', border:'1px solid #ef4444', marginBottom:'1.5rem'}}>
            <h3 style={{marginTop:0,fontSize:'1.125rem', display:'flex', alignItems:'center', gap:'0.5rem'}}><ClipboardList color="var(--danger)"/>Task Cleanup</h3>
            <p style={{fontSize:'0.875rem', color: '#dc2626', marginTop:0}}>
                ⚠️ WARNING: These actions permanently delete completed tasks and their data (photos, notes, purchases).
            </p>
            
            <div style={{marginBottom:'1rem'}}>
                <h4 style={{fontSize:'0.875rem', margin:'0 0 0.5rem 0', color:'#374151'}}>
                    Current Task Count: {data.tasks.length} total ({data.tasks.filter(t => t.isComplete).length} completed, {data.tasks.filter(t => !t.isComplete).length} pending)
                </h4>
                
                <div style={{display:'grid', gap:'0.5rem', fontSize:'0.75rem', color:'#6b7280'}}>
                    <div>• Tasks older than 1 month: {getOldTaskCount(1)} completed tasks</div>
                    <div>• Tasks older than 3 months: {getOldTaskCount(3)} completed tasks</div>
                    <div>• Tasks older than 6 months: {getOldTaskCount(6)} completed tasks</div>
                </div>
            </div>
            
            <div style={{display:'flex', gap:'0.5rem', flexWrap:'wrap'}}>
                <button 
                    onClick={() => handleDeleteOldTasks(1)} 
                    className="btn btn-warning"
                    disabled={getOldTaskCount(1) === 0}
                >
                    <Trash2 size={16}/>Delete Tasks Older Than 1 Month ({getOldTaskCount(1)})
                </button>
                <button 
                    onClick={() => handleDeleteOldTasks(3)} 
                    className="btn btn-warning"
                    disabled={getOldTaskCount(3) === 0}
                >
                    <Trash2 size={16}/>Delete Tasks Older Than 3 Months ({getOldTaskCount(3)})
                </button>
                <button 
                    onClick={() => handleDeleteOldTasks(6)} 
                    className="btn btn-danger"
                    disabled={getOldTaskCount(6) === 0}
                >
                    <Trash2 size={16}/>Delete Tasks Older Than 6 Months ({getOldTaskCount(6)})
                </button>
            </div>
        </div>

        <div style={{padding:'1rem',background:'#f3f4f6',borderRadius:'0.5rem'}}>
          <h4 style={{marginTop:0,fontSize:'1rem'}}>System Information</h4>
          <div style={{display:'grid',gap:'0.5rem',fontSize:'0.8125rem',color:'#6b7280'}}>
            <div>Total Tasks: {data.tasks.length} ({data.tasks.filter(t => t.isComplete).length} completed)</div>
            <div>Total Users: {users.length} ({users.filter(u => u.isActive).length} active)</div>
            <div>Version: 9.2.1</div>
          </div>
        </div>

        <div className="modal-actions" style={{justifyContent:'flex-end'}}>
          <button onClick={onClose} className="btn btn-secondary">
            <X size={16}/>Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Email Modal Component
const EmailTaskModal = ({ task, users, onSend, onClose }) => {
  const [recipients, setRecipients] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    const assignedEmails = task.assignedTo
      .map(name => users.find(u => u.name === name)?.email)
      .filter(Boolean)
      .join(', ');
    setRecipients(assignedEmails);
  }, [task, users]);

  const handleSend = async () => {
    if (!recipients.trim()) {
      alert("Please enter at least one recipient.");
      return;
    }
    setIsSending(true);
    await onSend(task.id, recipients, message);
    setIsSending(false);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Email Task: {task.title}</h2>
        </div>

        <div style={{display:'grid', gap:'0.75rem'}}>
          <div>
            <label className="form-label">To (comma-separated):</label>
            <input type="text" className="form-input" value={recipients}
              onChange={e => setRecipients(e.target.value)}
              placeholder="email1@example.com, email2@example.com"/>
          </div>
          <div>
            <label className="form-label">Additional Message:</label>
            <textarea className="form-textarea" value={message}
              onChange={e => setMessage(e.target.value)} rows="4"
              placeholder="Add any specific notes or instructions..."/>
          </div>
        </div>

        <div className="modal-actions">
          <button onClick={handleSend} className="btn btn-primary" disabled={isSending}>
            {isSending ? <Loader className="animate-spin" size={16}/> : <Send size={16}/>}
            Send Email
          </button>
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Scoreboard Component with Monthly Tracking
const ScoreboardView = ({ tasks, users, data, onUpdateData, currentUser, isAdmin }) => {
  const [activeTab, setActiveTab] = useState('monthly');
  // Get current month/year and calculate period boundaries
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const monthStart = new Date(currentYear, currentMonth, 1);
  const monthEnd = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59);
  
  // Get week boundaries
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
  weekStart.setHours(0, 0, 0, 0);
  
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);

  // Initialize leaderboard history if it doesn't exist
  const leaderboardHistory = data.leaderboardHistory || {
    monthlyWinners: [],
    weeklyWinners: [],
    lastMonthlyReset: null,
    lastWeeklyReset: null
  };

  // Check if we need to archive current month's winner and reset
  const needsMonthlyReset = !leaderboardHistory.lastMonthlyReset || 
    new Date(leaderboardHistory.lastMonthlyReset).getMonth() !== currentMonth ||
    new Date(leaderboardHistory.lastMonthlyReset).getFullYear() !== currentYear;

  // Calculate scores for current period
  const calculateScores = (startDate, endDate, label) => {
    const scores = {};
    
    // Initialize scores for all active users
    users.filter(u => u.isActive !== false).forEach(user => {
      scores[user.name] = {
        name: user.name,
        tasksCompleted: 0,
        tasksCreated: 0,
        notesAdded: 0,
        photosUploaded: 0,
        purchasesLogged: 0,
        totalScore: 0,
        recentActivity: [],
        period: label
      };
    });

    // Process all tasks
    tasks.forEach(task => {
      // Points for completing tasks (if completed in period)
      if (task.isComplete && task.completedAt) {
        const completedDate = new Date(task.completedAt);
        if (completedDate >= startDate && completedDate <= endDate) {
          task.assignedTo.forEach(assignee => {
            if (scores[assignee]) {
              scores[assignee].tasksCompleted++;
              scores[assignee].totalScore += 10; // 10 points per completed task
            }
          });
        }
      }

      // Points for timeline activities in the period
      if (task.timeline) {
        task.timeline.forEach(entry => {
          if (entry.timestamp && scores[entry.user]) {
            const entryDate = new Date(entry.timestamp);
            if (entryDate >= startDate && entryDate <= endDate) {
              if (entry.type === 'created') {
                scores[entry.user].tasksCreated++;
                scores[entry.user].totalScore += 3; // 3 points per created task
              } else if (entry.type === 'note') {
                scores[entry.user].notesAdded++;
                scores[entry.user].totalScore += 2; // 2 points per note
              } else if (entry.type === 'photo') {
                scores[entry.user].photosUploaded++;
                scores[entry.user].totalScore += 3; // 3 points per photo
              } else if (entry.type === 'purchase') {
                scores[entry.user].purchasesLogged++;
                scores[entry.user].totalScore += 4; // 4 points per purchase
              }
              
              scores[entry.user].recentActivity.push(entry);
            }
          }
        });
      }
    });

    // Convert to array and sort by total score
    return Object.values(scores).sort((a, b) => b.totalScore - a.totalScore);
  };

  // Calculate current month and week scores
  const monthlyScores = calculateScores(monthStart, monthEnd, `${monthStart.toLocaleDateString('en', { month: 'long', year: 'numeric' })}`);
  const weeklyScores = calculateScores(weekStart, weekEnd, `Week of ${weekStart.toLocaleDateString()}`);

  // Auto-archive winners and reset if needed (admin only)
  const handleMonthlyReset = useCallback(async () => {
    if (!isAdmin || monthlyScores.length === 0) return;

    const winner = monthlyScores[0];
    const lastMonth = new Date(currentYear, currentMonth - 1, 1);
    const monthName = lastMonth.toLocaleDateString('en', { month: 'long', year: 'numeric' });
    
    const newHistory = {
      ...leaderboardHistory,
      monthlyWinners: [
        {
          month: monthName,
          winner: winner.name,
          score: winner.totalScore,
          date: new Date().toISOString(),
          topThree: monthlyScores.slice(0, 3).map(u => ({ name: u.name, score: u.totalScore }))
        },
        ...leaderboardHistory.monthlyWinners
      ].slice(0, 12), // Keep last 12 months
      lastMonthlyReset: new Date().toISOString()
    };

    const updatedData = { ...data, leaderboardHistory: newHistory };
    await onUpdateData(updatedData);
  }, [isAdmin, monthlyScores, currentYear, currentMonth, leaderboardHistory, data, onUpdateData]);

  // Manual reset function for admins
  const manualMonthlyReset = useCallback(async () => {
    if (!isAdmin || !window.confirm('Are you sure you want to reset the monthly leaderboard and archive the current winner?')) return;
    await handleMonthlyReset();
  }, [isAdmin, handleMonthlyReset]);

  // Get top 3 performers for current month
  const topPerformers = monthlyScores.slice(0, 3);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Month Info and Admin Controls */}
      <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>
              🏆 {monthStart.toLocaleDateString('en', { month: 'long', year: 'numeric' })} Leaderboard
            </h2>
            <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.875rem' }}>
              Competition resets monthly • Everyone starts fresh!
            </p>
          </div>
          {isAdmin && (
            <button onClick={manualMonthlyReset} className="btn" 
              style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
              <Trophy size={16} />Archive & Reset Month
            </button>
          )}
        </div>
      </div>

      {/* Current Month Top Performers Podium */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ marginTop: 0, fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Trophy size={24} color="#FFD700" />
          This Month's Champions
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {topPerformers.map((user, index) => (
            <div key={user.name} style={{
              background: index === 0 ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' : 
                        index === 1 ? 'linear-gradient(135deg, #C0C0C0 0%, #808080 100%)' :
                        'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)',
              color: 'white',
              padding: '1.5rem',
              borderRadius: '1rem',
              textAlign: 'center',
              transform: index === 0 ? 'scale(1.05)' : 'scale(1)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>
                {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
              </div>
              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>{user.name}</h3>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{user.totalScore}</div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>points</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs for Different Views */}
      <div className="card">
        <div className="tab-container">
          <button onClick={() => setActiveTab('monthly')}
            className={`tab-button ${activeTab === 'monthly' ? 'active' : ''}`}>
            📅 This Month
          </button>
          <button onClick={() => setActiveTab('weekly')}
            className={`tab-button ${activeTab === 'weekly' ? 'active' : ''}`}>
            📊 This Week
          </button>
          <button onClick={() => setActiveTab('halloffame')}
            className={`tab-button ${activeTab === 'halloffame' ? 'active' : ''}`}>
            🏛️ Hall of Fame
          </button>
        </div>

        {activeTab === 'monthly' && (
          <div>
            <h2 style={{ marginTop: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Star size={20} />
              Monthly Leaderboard
            </h2>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Rank</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Tasks Done</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Notes</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Photos</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Purchases</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Total Score</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyScores.map((user, index) => (
                    <tr key={user.name} style={{ 
                      borderBottom: '1px solid var(--gray-200)',
                      background: index % 2 === 0 ? 'transparent' : 'var(--gray-100)'
                    }}>
                      <td style={{ padding: '0.75rem', fontWeight: 'bold' }}>
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                      </td>
                      <td style={{ padding: '0.75rem', fontWeight: '600' }}>
                        {user.name}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {user.tasksCompleted}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {user.notesAdded}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {user.photosUploaded}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {user.purchasesLogged}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '1.125rem', fontWeight: 'bold', color: 'var(--primary)' }}>
                        {user.totalScore}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'weekly' && (
          <div>
            <h2 style={{ marginTop: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <TrendingUp size={20} />
              This Week's Progress
            </h2>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Rank</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Tasks Done</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Activity</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Weekly Score</th>
                  </tr>
                </thead>
                <tbody>
                  {weeklyScores.map((user, index) => (
                    <tr key={user.name} style={{ 
                      borderBottom: '1px solid var(--gray-200)',
                      background: index % 2 === 0 ? 'transparent' : 'var(--gray-100)'
                    }}>
                      <td style={{ padding: '0.75rem', fontWeight: 'bold' }}>
                        {index === 0 ? '⭐' : index + 1}
                      </td>
                      <td style={{ padding: '0.75rem', fontWeight: '600' }}>
                        {user.name}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {user.tasksCompleted}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {user.recentActivity.length}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '1.125rem', fontWeight: 'bold', color: 'var(--success)' }}>
                        {user.totalScore}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'halloffame' && (
          <div>
            <h2 style={{ marginTop: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Award size={20} />
              Hall of Fame - Past Champions
            </h2>
            
            {leaderboardHistory.monthlyWinners.length > 0 ? (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {leaderboardHistory.monthlyWinners.map((record, index) => (
                  <div key={index} style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    border: '2px solid #e2e8f0',
                    borderRadius: '0.75rem',
                    padding: '1.5rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 style={{ margin: 0, fontSize: '1.125rem', color: '#1e293b' }}>
                        🏆 {record.month}
                      </h3>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        {new Date(record.date).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                      {record.topThree.map((user, pos) => (
                        <div key={user.name} style={{
                          background: pos === 0 ? '#fef3c7' : pos === 1 ? '#f3f4f6' : '#fecaca',
                          padding: '1rem',
                          borderRadius: '0.5rem',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                            {pos === 0 ? '🥇' : pos === 1 ? '🥈' : '🥉'}
                          </div>
                          <div style={{ fontWeight: 'bold' }}>{user.name}</div>
                          <div style={{ fontSize: '0.875rem', color: '#64748b' }}>{user.score} pts</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                <Trophy size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                <p>No champions yet! Be the first to win a monthly competition.</p>
              </div>
            )}
          </div>
        )}

        {/* Scoring Legend */}
        <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--gray-100)', borderRadius: '0.5rem' }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem' }}>How Points Are Earned:</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.5rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
            <div>✓ Complete Task: <strong>10 pts</strong></div>
            <div>📝 Add Note: <strong>2 pts</strong></div>
            <div>📷 Upload Photo: <strong>3 pts</strong></div>
            <div>💰 Log Purchase: <strong>4 pts</strong></div>
            <div>➕ Create Task: <strong>3 pts</strong></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [data, setData] = useState({ tasks: [], users: [], loginStatus: {} });
  const { tasks, users, loginStatus } = data;
  const [currentUser, setCurrentUser] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadingPurchase, setUploadingPurchase] = useState(false);
  const [photoModalUrl, setPhotoModalUrl] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [currentView, setCurrentView] = useState('tasks');
  const [showPassword, setShowPassword] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [emailingTask, setEmailingTask] = useState(null);
  const [taskViewedTimes, setTaskViewedTimes] = useState({});

  const activeUserNames = useMemo(() => {
    return users.filter(u => u.isActive !== false).map(u => u.name).sort();
  }, [users]);

  const isAdmin = useMemo(() => {
    const userObj = users.find(u => u.name === currentUser);
    return userObj && userObj.role === 'admin';
  }, [currentUser, users]);

  useEffect(() => {
    if (currentUser) {
      const storedViews = localStorage.getItem(`taskViews_${currentUser}`);
      if (storedViews) {
        setTaskViewedTimes(JSON.parse(storedViews));
      }
    }
  }, [currentUser]);

  const markTaskAsViewed = useCallback((taskId) => {
    const now = new Date().toISOString();
    setTaskViewedTimes(prev => {
      const updated = { ...prev, [taskId]: now };
      localStorage.setItem(`taskViews_${currentUser}`, JSON.stringify(updated));
      return updated;
    });
  }, [currentUser]);

  const taskHasNewNotes = useCallback((task) => {
    if (!task.notes || task.notes.length === 0) return false;

    const lastViewTime = taskViewedTimes[task.id];
    if (!lastViewTime) return true;

    const lastNote = task.notes[task.notes.length - 1];
    return isAfter(new Date(lastNote.timestamp), new Date(lastViewTime));
  }, [taskViewedTimes]);

  const loadData = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api.php?action=getData&_=${Date.now()}`);
      if (r.ok) {
        const d = await r.json();
        const updatedTasks = d.tasks.map(task => {
            if (task.photos && task.photos.length > 0) {
              return {
                  ...task,
                  photos: task.photos.map(photo => {
                      const photoObj = typeof photo === 'string' ? { filename: photo } : photo;
                      return {
                          ...photoObj,
                          displayName: photoObj.displayName || (photoObj.filename.toLowerCase().endsWith('.pdf') ? 'Document' : 'Photo')
                      };
                  })
              };
            }
            return task;
        });

        const finalTasks = updatedTasks.map(task => {
            if (task.timeline && task.timeline.length > 0) {
              return {
                  ...task,
                  timeline: task.timeline.map(entry => {
                      if (entry.type === 'photo' && entry.content && !entry.displayName) {
                          const relatedPhoto = task.photos.find(p => p.filename === entry.content);
                          return {
                              ...entry,
                              displayName: relatedPhoto?.displayName || (entry.content.toLowerCase().endsWith('.pdf') ? 'Document' : 'Photo')
                          };
                      }
                      return entry;
                  })
              };
            }
            return task;
        });

        const tasksWithBooleanIsComplete = finalTasks.map(task => ({
            ...task,
            isComplete: !!task.isComplete
        }));

        setData({ ...d, tasks: tasksWithBooleanIsComplete });
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const saveData = useCallback((newData) => {
    setData(newData);
    fetch(`${API_BASE}/api.php?action=saveData`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newData)
    }).catch(e => console.error("Save failed", e));
  }, []);
  
  const handleSendMentionEmail = useCallback(async (task, note, mentionedUsers) => {
    const recipients = mentionedUsers
        .map(name => users.find(u => u.name === name)?.email)
        .filter(Boolean)
        .join(',');

    if (!recipients) return;
    
    try {
      const response = await fetch(`${API_BASE}/api.php?action=sendEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task,
          recipients,
          message: note,
          sender: currentUser,
          type: 'mention'
        })
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Mention email failed to send.');
      }
      console.log('Mention emails sent successfully!');
    } catch (error) {
      alert(`Note saved, but failed to send mention email: ${error.message}`);
    }
  }, [users, currentUser]);

  const handleUpdate = useCallback((taskId, updates, mentionedUsers = []) => {
    let mentionedTask = null;
    let newNoteContent = updates.notes ? updates.notes[updates.notes.length - 1].content : null;

    const newTasks = tasks.map(t => {
      if (t.id === taskId) {
        const updatedTask = { ...t, ...updates };
        if (updates.isComplete !== undefined && updates.isComplete !== t.isComplete) {
          updatedTask.timeline = [...(updatedTask.timeline || []), {
            id: Date.now(),
            type: updates.isComplete ? 'completed' : 'reopened',
            user: currentUser,
            timestamp: new Date().toISOString(),
            content: `Task ${updates.isComplete ? 'completed' : 'reopened'}`
          }];
          updatedTask.completedBy = updates.isComplete ? currentUser : null;
          updatedTask.completedAt = updates.isComplete ? new Date().toISOString() : null;

          if (updates.isComplete && t.recurrence && t.recurrence !== 'none') {
            const nextDate = getNextRecurrenceDate(t.dueDate || new Date(), t.recurrence, t.monthlyDay);
            const newRecurringTask = {
              ...t, id: Date.now() + 1, title: t.title, description: t.description,
              priority: t.priority, assignedTo: t.assignedTo, recurrence: t.recurrence,
              monthlyDay: t.monthlyDay, dueDate: nextDate.toISOString().split('T')[0],
              creator: currentUser, createdAt: new Date().toISOString(), isComplete: false,
              isArchived: false, isFlagged: false, photos: [], notes: [], purchases: [],
              completedBy: null, completedAt: null,
              timeline: [{
                id: Date.now(), type: 'created', user: currentUser,
                timestamp: new Date().toISOString(), content: `Recurring task created from: ${t.title}`
              }]
            };
            newTasks.push(newRecurringTask);
          }
        }
        if (mentionedUsers.length > 0) {
            mentionedTask = updatedTask;
        }
        return updatedTask;
      }
      return t;
    });
    
    saveData({ ...data, tasks: newTasks });
    
    if (mentionedTask && newNoteContent) {
        handleSendMentionEmail(mentionedTask, newNoteContent, mentionedUsers);
    }
  }, [data, currentUser, saveData, tasks, handleSendMentionEmail]);

  const uploadPhotos = useCallback(async (taskId, files) => {
    const photoEntries = [];
    const timelineEntries = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isPdf = file.type === 'application/pdf';
      const fileTypeLabel = isPdf ? 'document' : 'photo';

      setUploadProgress({
        current: i + 1,
        total: files.length,
        fileName: file.name,
        status: `Uploading ${fileTypeLabel}...`
      });

      const formData = new FormData();
      formData.append('photo', file);
      formData.append('taskId', taskId);
      formData.append('user', currentUser);

      if (file.customName) {
        formData.append('displayName', file.customName);
      }

      try {
        const response = await fetch(`${API_BASE}/api.php?action=uploadPhoto`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.filename) {
            photoEntries.push({
              id: Date.now() + Math.random(),
              filename: result.filename,
              displayName: result.displayName || null,
              uploadedBy: currentUser,
              uploadedAt: new Date().toISOString(),
              fileType: fileTypeLabel
            });
            timelineEntries.push({
              id: Date.now() + Math.random(),
              type: 'photo',
              user: currentUser,
              timestamp: new Date().toISOString(),
              content: result.filename,
              displayName: result.displayName || null
            });
            alert(`${fileTypeLabel} uploaded successfully!`);
          } else if (result.error) {
            alert(result.error);
            setUploadProgress(null);
            return;
          }
        } else {
          const error = await response.json();
          alert(error.error || 'Upload failed');
          setUploadProgress(null);
          return;
        }
      } catch (e) {
        console.error('Upload error:', e);
        alert(`${fileTypeLabel} upload failed due to network error.`);
      }
    }

    setUploadProgress(null);
    return { photoEntries, timelineEntries };
  }, [currentUser]);

  const createTask = useCallback(async (taskData, files) => {
    const newId = Date.now();

    if (!taskData.assignedTo.includes(currentUser)) {
      taskData.assignedTo.push(currentUser);
    }

    let newTask = {
      id: newId,
      ...taskData,
      creator: currentUser,
      createdAt: new Date().toISOString(),
      isComplete: false,
      isArchived: false,
      isFlagged: false,
      photos: [],
      notes: [],
      purchases: [],
      timeline: [{
        id: Date.now(),
        type: 'created',
        user: currentUser,
        timestamp: new Date().toISOString(),
        content: 'Task created'
      }]
    };

    const currentTasks = [...tasks];
    currentTasks.push(newTask);
    setData(prevData => ({ ...prevData, tasks: currentTasks }));

    if (files?.length > 0) {
      const uploads = await uploadPhotos(newId, files);
      newTask.photos = uploads.photoEntries;
      newTask.timeline.push(...uploads.timelineEntries);
    }

    saveData({ ...data, tasks: currentTasks });
    setShowNewTaskModal(false);
  }, [data, currentUser, saveData, uploadPhotos, tasks]);

  const handlePhotoUploadForTask = useCallback(async (taskId, files) => {
    const uploads = await uploadPhotos(taskId, files);
    if (uploads.photoEntries.length) {
      const newTasks = tasks.map(t => t.id === taskId ? {
        ...t,
        photos: [...(t.photos || []), ...uploads.photoEntries],
        timeline: [...(t.timeline || []), ...uploads.timelineEntries]
      } : t);
      saveData({ ...data, tasks: newTasks });
    }
  }, [data, saveData, uploadPhotos, tasks]);

  const handleCompleteTask = useCallback(async (task) => {
    if (!window.confirm('Complete this task and notify assignees?')) return;

    const updatedTaskForEmail = {
        ...task,
        isComplete: true,
        completedBy: currentUser,
        completedAt: new Date().toISOString()
    };
    
    handleUpdate(task.id, updatedTaskForEmail);

    const assignedEmails = updatedTaskForEmail.assignedTo
      .map(name => users.find(u => u.name === name)?.email)
      .filter(Boolean)
      .join(', ');

    if (assignedEmails) {
      try {
        const response = await fetch(`${API_BASE}/api.php?action=sendEmail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            task: updatedTaskForEmail,
            recipients: assignedEmails,
            message: `Task "${updatedTaskForEmail.title}" has been marked as complete by ${currentUser} at ${formatDateTime(updatedTaskForEmail.completedAt)}.`,
            sender: currentUser,
            type: 'completion'
          })
        });
        const result = await response.json();
        if (response.ok && result.success) {
            alert('Task completed and notifications sent!');
        } else {
            throw new Error(result.error || 'Email sending failed.');
        }
      } catch (error) {
        console.error("Email send error:", error);
        alert(`Task completed, but email notification failed: ${error.message}`);
      }
    } else {
        alert('Task completed. No recipients for email notification.');
    }
  }, [users, currentUser, handleUpdate]);

  const handleSendEmail = useCallback(async (taskId, recipients, message) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    try {
      const response = await fetch(`${API_BASE}/api.php?action=sendEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task,
          recipients,
          message,
          sender: currentUser,
          type: 'manual'
        })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Email failed to send.');
      }

      alert('Email sent successfully!');
    } catch (error) {
      alert(`Failed to send email: ${error.message}`);
    }
  }, [tasks, currentUser]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const username = e.target.elements[0].value;
    const password = e.target.elements[1].value;

    const user = users.find(u => u.name.toLowerCase() === username.toLowerCase());

    if (user && (user.isActive !== false) && user.password === password) {
      localStorage.setItem('maintenanceUser', user.name);
      setCurrentUser(user.name);
      setLoginError('');
      
      // Immediately update login timestamp
      try {
        await fetch(`${API_BASE}/api.php?action=updateLogin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user: user.name })
        });
        // Wait a bit then reload to show updated timestamp
        setTimeout(() => loadData(), 1000);
      } catch (error) {
        console.error('Failed to update login timestamp:', error);
      }
    } else if (user && user.isActive === false) {
      setLoginError('This account has been deactivated.');
    } else {
      setLoginError('Invalid credentials');
    }
  };

  useEffect(() => {
    const u = localStorage.getItem('maintenanceUser');
    if (u) {
      setCurrentUser(u);
      // Update login timestamp for auto-login
      fetch(`${API_BASE}/api.php?action=updateLogin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: u })
      }).catch(() => {});
    }
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (currentUser) {
      const updateLogin = () => fetch(`${API_BASE}/api.php?action=updateLogin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: currentUser })
      }).catch(() => {});

      const heartbeat = () => fetch(`${API_BASE}/api.php?action=heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: currentUser })
      }).catch(() => {});

      updateLogin().then(() => loadData());
      const i = setInterval(loadData, SYNC_INTERVAL);
      const h = setInterval(heartbeat, 60000);

      return () => {
        clearInterval(i);
        clearInterval(h);
      };
    }
  }, [currentUser, loadData]);

  useEffect(() => {
    if (selectedTask) {
      setSelectedTask(t => tasks.find(i => i.id === t?.id));
    }
  }, [tasks, selectedTask]);

  const visibleTasks = useMemo(() =>
    tasks.filter(t =>
      !t.isArchived &&
      (showCompleted || !t.isComplete) &&
      t.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (filterPriority === 'all' || t.priority === filterPriority) &&
      (filterAssignee === 'all' || t.assignedTo.includes(filterAssignee) ||
       (filterAssignee === 'mine' && t.assignedTo.includes(currentUser)))
    ).sort((a, b) => {
        const aHasNew = taskHasNewNotes(a);
        const bHasNew = taskHasNewNotes(b);
        if (aHasNew && !bHasNew) return -1;
        if (!aHasNew && bHasNew) return 1;

        if (a.isFlagged && !b.isFlagged) return -1;
        if (!a.isFlagged && b.isFlagged) return 1;

        if (a.isComplete && !b.isComplete) return 1;
        if (!a.isComplete && b.isComplete) return -1;

        const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3 };
        const pA = priorityOrder[a.priority] || 99;
        const pB = priorityOrder[b.priority] || 99;
        if (pA !== pB) return pA - pB;

        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return dateA - dateB;
    }),
    [tasks, showCompleted, searchTerm, filterPriority, filterAssignee, currentUser, taskHasNewNotes]
  );

  const myDashboardTasks = useMemo(() =>
    tasks.filter(t => !t.isComplete && !t.isArchived && t.assignedTo.includes(currentUser)),
    [tasks, currentUser]
  );

  if (!currentUser) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div className="card" style={{width:'100%',maxWidth:'400px',padding:'2rem'}}>
          <div style={{textAlign:'center',marginBottom:'2rem'}}>
            <Wrench size={48} color="var(--primary)"/>
            <h1 style={{margin:'1rem 0 0 0',fontSize:'1.75rem'}}>Kool-a-Chem Tasks</h1>
          </div>
          <form onSubmit={handleLogin}>
            <div style={{marginBottom:'1rem'}}>
              <label className="form-label">Username</label>
              <input className="form-input" type="text"
                defaultValue={localStorage.getItem('maintenanceUser') || ''} required/>
            </div>
            <div style={{marginBottom:'1rem'}}>
              <label className="form-label">Password</label>
              <div style={{position:'relative'}}>
                <input className="form-input" type={showPassword ? "text" : "password"}
                  required style={{paddingRight:'3rem'}}/>
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position:'absolute',
                    right:'0.25rem',
                    top:'50%',
                    transform:'translateY(-50%)',
                    background:'transparent',
                    border:'none',
                    color:'#6b7280',
                    cursor:'pointer',
                    padding:'0.5rem'
                  }}>
                  {showPassword ? <EyeOff size={20}/> : <Eye size={20}/>}
                </button>
              </div>
            </div>
            {loginError && <p style={{color:'var(--danger)',fontSize:'0.875rem',marginBottom:'1rem'}}>{loginError}</p>}
            <button type="submit" className="btn btn-primary" style={{width:'100%',padding:'0.875rem'}}>
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main app UI
  return (
    <div style={{minHeight:'100vh',display:'flex',flexDirection:'column'}}>
      <UploadIndicator p={uploadProgress} uploadingPurchase={uploadingPurchase}/>

      <header className="header">
        <div className="header-content">
          <h1 className="header-title">Kool-a-Chem</h1>
          <div className="header-actions">
            <span className="header-user">{currentUser}</span>
            {isAdmin && (
              <button onClick={() => setShowAdminPanel(true)} className="btn btn-header">
                <ShieldCheck size={16}/>
                <span style={{display:window.innerWidth>600?'inline':'none'}}>Admin</span>
              </button>
            )}
            <button onClick={() => setCurrentView('tasks')}
              className={`btn btn-header ${currentView === 'tasks' ? 'active' : ''}`}>
              <ClipboardList size={16}/>
              <span style={{display:window.innerWidth>600?'inline':'none'}}>Tasks</span>
            </button>
            <button onClick={() => setCurrentView('dashboard')}
              className={`btn btn-header ${currentView === 'dashboard' ? 'active' : ''}`}>
              <BarChart3 size={16}/>
              <span style={{display:window.innerWidth>600?'inline':'none'}}>Dashboard</span>
            </button>
            <button onClick={() => setCurrentView('scoreboard')}
              className={`btn btn-header ${currentView === 'scoreboard' ? 'active' : ''}`}>
              <Trophy size={16}/>
              <span style={{display:window.innerWidth>600?'inline':'none'}}>Scoreboard</span>
            </button>
            <button onClick={() => {
              localStorage.removeItem('maintenanceUser');
              setCurrentUser(null);
            }} className="btn btn-header">
              <LogOut size={16}/>
              <span style={{display:window.innerWidth>600?'inline':'none'}}>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="content">
        {currentView === 'tasks' ? (
          <>
            <div className="card">
              <div className="search-wrapper" style={{marginBottom:'0.75rem'}}>
                <Search size={18} className="search-icon"/>
                <input type="text" className="form-input search-input"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}/>
              </div>

              <div className="filters-row">
                <select className="form-input filter-select" value={filterAssignee}
                  onChange={e => setFilterAssignee(e.target.value)}>
                  <option value="all">All Assignees</option>
                  <option value="mine">My Tasks</option>
                  {activeUserNames.map(m => <option key={m}>{m}</option>)}
                </select>
                <select className="form-input filter-select" value={filterPriority}
                  onChange={e => setFilterPriority(e.target.value)}>
                  <option value="all">All Priorities</option>
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
                <button onClick={() => setShowCompleted(s => !s)} className="btn"
                  style={{background:showCompleted?'var(--success)':'#6b7280',color:'white'}}>
                  <Eye size={16}/>{showCompleted ? 'Hide' : 'Show'} Done
                </button>
                <button onClick={() => loadData()} className="btn" title="Refresh">
                  <RefreshCw size={16}/>
                </button>
                <button onClick={() => setShowNewTaskModal(true)} className="btn btn-primary">
                  <Plus size={16}/>New
                </button>
              </div>
            </div>

            <div style={{display:'grid',gap:'0.75rem'}}>
              {visibleTasks.length > 0 ? (
                visibleTasks.map(t =>
                  <TaskItem key={t.id} task={t} onSelect={() => setSelectedTask(t)}
                    onUpdate={handleUpdate} currentUser={currentUser}
                    hasNewNotes={taskHasNewNotes(t)}/>
                )
              ) : (
                <div className="card" style={{textAlign:'center',color:'#6b7280',padding:'2rem'}}>
                  No tasks match your filters.
                </div>
              )}
            </div>
          </>
        ) : currentView === 'dashboard' ? (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:'1rem'}}>
            <div className="card">
              <h2 style={{marginTop:0,fontSize:'1.25rem'}}>My Outstanding Tasks</h2>
              {myDashboardTasks.length > 0 ? (
                <div style={{display:'grid',gap:'0.75rem'}}>
                  {myDashboardTasks.map(t =>
                    <TaskItem key={t.id} task={t} onSelect={() => setSelectedTask(t)}
                      onUpdate={handleUpdate} currentUser={currentUser}
                      hasNewNotes={taskHasNewNotes(t)}/>
                  )}
                </div>
              ) : (
                <p style={{color:'#6b7280',fontSize:'0.875rem'}}>You have no outstanding tasks.</p>
              )}
            </div>

            <div className="card">
              <h2 style={{marginTop:0,fontSize:'1.25rem'}}>Team Status</h2>
              <div style={{display:'grid',gap:'0.5rem'}}>
                {users.filter(u => u.isActive !== false).map(u => {
                  const info = loginStatus[u.name] || {};
                  const isOnline = info.lastActive &&
                    (new Date() - new Date(info.lastActive)) < ACTIVITY_TIMEOUT;

                  return (
                    <div key={u.id} style={{
                      display:'flex',
                      alignItems:'center',
                      gap:'0.75rem',
                      padding:'0.75rem',
                      background:'#f8f9fa',
                      borderRadius:'0.5rem',
                      fontSize:'0.875rem'
                    }}>
                      <div style={{
                        width:'0.5rem',
                        height:'0.5rem',
                        borderRadius:'50%',
                        background: isOnline ? '#10b981' : '#6b7280'
                      }}/>
                      <div>
                        <strong>{u.name}</strong>
                        <div style={{fontSize:'0.75rem',color:'#6b7280'}}>
                          Last: {timeAgo(info.lastLogin)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card" style={{gridColumn:'1 / -1'}}>
              <h2 style={{marginTop:0,fontSize:'1.25rem',marginBottom:'1rem',display:'flex',alignItems:'center',gap:'0.5rem'}}>
                <span>📚</span> User Guide & Features
              </h2>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:'1.5rem',fontSize:'0.875rem'}}>
                
                <div>
                  <h3 style={{fontSize:'1rem',marginBottom:'0.75rem',color:'#1f2937',display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    <span>📋</span> Task Management
                  </h3>
                  <ul style={{margin:0,paddingLeft:'1.25rem',lineHeight:'1.6'}}>
                    <li><strong>Create Tasks:</strong> Click "Add New Task" button, fill details, assign users</li>
                    <li><strong>View Tasks:</strong> Browse all tasks, filter by assigned user or status</li>
                    <li><strong>Update Tasks:</strong> Click any task to edit, add notes, upload photos</li>
                    <li><strong>Complete Tasks:</strong> Mark as complete with optional completion notes</li>
                    <li><strong>Photo Uploads:</strong> Drag & drop or paste screenshots directly into task notes</li>
                  </ul>
                </div>

                <div>
                  <h3 style={{fontSize:'1rem',marginBottom:'0.75rem',color:'#1f2937',display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    <span>💬</span> Team Communication
                  </h3>
                  <ul style={{margin:0,paddingLeft:'1.25rem',lineHeight:'1.6'}}>
                    <li><strong>Live Chat:</strong> Click chat icon (bottom right) for real-time team communication</li>
                    <li><strong>Mentions:</strong> Type <code style={{background:'#f3f4f6',padding:'0.125rem 0.25rem',borderRadius:'0.25rem'}}>@username</code> to mention team members</li>
                    <li><strong>Task Notes:</strong> Add notes to tasks, mention users to send email notifications</li>
                    <li><strong>Email Alerts:</strong> Get notified when mentioned or assigned to tasks</li>
                  </ul>
                </div>

                <div>
                  <h3 style={{fontSize:'1rem',marginBottom:'0.75rem',color:'#1f2937',display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    <span>🏆</span> Productivity Features
                  </h3>
                  <ul style={{margin:0,paddingLeft:'1.25rem',lineHeight:'1.6'}}>
                    <li><strong>Scoreboard:</strong> Track team productivity with points for completed tasks</li>
                    <li><strong>Monthly Competitions:</strong> Compete for monthly leaderboard positions</li>
                    <li><strong>Hall of Fame:</strong> View past monthly winners and achievements</li>
                    <li><strong>Activity Tracking:</strong> See who's online and when they last logged in</li>
                  </ul>
                </div>

                <div>
                  <h3 style={{fontSize:'1rem',marginBottom:'0.75rem',color:'#1f2937',display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    <span>⚙️</span> System Features
                  </h3>
                  <ul style={{margin:0,paddingLeft:'1.25rem',lineHeight:'1.6'}}>
                    <li><strong>Mobile Ready:</strong> Works on all devices - install as PWA app</li>
                    <li><strong>Real-time Updates:</strong> See live changes without refreshing</li>
                    <li><strong>Search & Filter:</strong> Quickly find tasks using search bar</li>
                    <li><strong>Dashboard:</strong> Personal view of your assigned tasks and team status</li>
                    <li><strong>Admin Panel:</strong> Admins can manage users, clean old tasks</li>
                  </ul>
                </div>

                <div>
                  <h3 style={{fontSize:'1rem',marginBottom:'0.75rem',color:'#1f2937',display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    <span>🎯</span> Quick Tips
                  </h3>
                  <ul style={{margin:0,paddingLeft:'1.25rem',lineHeight:'1.6'}}>
                    <li><strong>Keyboard Shortcuts:</strong> Use Tab/Enter to select mentions in chat</li>
                    <li><strong>Photo Tips:</strong> Screenshots auto-resize for optimal storage</li>
                    <li><strong>Priority Tasks:</strong> Use urgent/high priority for important items</li>
                    <li><strong>Status Colors:</strong> Green dot = online, gray = offline in team status</li>
                    <li><strong>Mentions:</strong> Chat button pulses red when you're mentioned</li>
                  </ul>
                </div>

                <div>
                  <h3 style={{fontSize:'1rem',marginBottom:'0.75rem',color:'#1f2937',display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    <span>📱</span> Mobile Installation
                  </h3>
                  <ul style={{margin:0,paddingLeft:'1.25rem',lineHeight:'1.6'}}>
                    <li><strong>iPhone/iPad:</strong> Safari → Share → "Add to Home Screen"</li>
                    <li><strong>Android:</strong> Chrome → Menu → "Add to Home screen"</li>
                    <li><strong>Desktop:</strong> Look for install icon in browser address bar</li>
                    <li><strong>Benefits:</strong> Faster loading, offline capability, native app feel</li>
                  </ul>
                </div>

              </div>
              
              <div style={{marginTop:'1.5rem',padding:'1rem',background:'#eff6ff',borderRadius:'0.5rem',border:'1px solid #dbeafe'}}>
                <p style={{margin:0,fontSize:'0.875rem',color:'#1e40af'}}>
                  <strong>💡 Pro Tip:</strong> This is a Progressive Web App (PWA) - install it on your device for the best experience! 
                  It works offline and provides native app-like functionality.
                </p>
              </div>
            </div>
          </div>
        ) : currentView === 'scoreboard' ? (
          <ScoreboardView 
            tasks={tasks} 
            users={users} 
            data={data}
            onUpdateData={saveData}
            currentUser={currentUser}
            isAdmin={isAdmin}
          />
        ) : null}
      </main>

      {(selectedTask || showNewTaskModal) && (
        <TaskDetailModal
          task={selectedTask}
          currentUser={currentUser}
          allUsers={activeUserNames}
          onClose={() => {
            setSelectedTask(null);
            setShowNewTaskModal(false);
          }}
          onCreate={createTask}
          onUpdate={handleUpdate}
          onPhotoUpload={handlePhotoUploadForTask}
          loadData={loadData}
          isAdmin={isAdmin}
          setPhotoModalUrl={setPhotoModalUrl}
          onEmailTask={setEmailingTask}
          onCompleteTask={handleCompleteTask}
          setUploadingPurchase={setUploadingPurchase}
          markTaskAsViewed={markTaskAsViewed}
        />
      )}

      {showAdminPanel && isAdmin && (
        <AdminPanel
          data={data}
          currentUser={currentUser}
          onUpdateData={saveData}
          onClose={() => setShowAdminPanel(false)}
        />
      )}

      {emailingTask && (
        <EmailTaskModal
          task={emailingTask}
          users={users}
          onSend={handleSendEmail}
          onClose={() => setEmailingTask(null)}
        />
      )}

      {photoModalUrl && (
        <div className="modal-overlay" onClick={() => setPhotoModalUrl(null)}>
          <img src={photoModalUrl} alt="Full size"
            style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:'0.5rem',objectFit:'contain'}}/>
        </div>
      )}

      {currentUser && <ChatBox currentUser={currentUser} allUsers={users.map(u => u.name)} />}
    </div>
  );
}

export default App;
